/**
 * UploadDocumentModal - upload a document to a deal room.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  AccessTier,
  DealRoomDocument,
  DealRoomFileType,
} from '../types/dealRoom';

interface UploadDocumentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    file: File;
    access_tier: AccessTier;
    file_type: DealRoomFileType;
    previous_document_id?: number;
  }) => Promise<void> | void;
  existingDocuments?: DealRoomDocument[];
  defaultTier?: AccessTier;
  defaultFile?: File | null;
}

const FILE_TYPE_OPTIONS: Array<{ value: DealRoomFileType; label: string }> = [
  { value: 'pitch_deck', label: 'Pitch deck' },
  { value: 'executive_summary', label: 'Executive summary' },
  { value: 'financials', label: 'Financials' },
  { value: 'cap_table', label: 'Cap table' },
  { value: 'product_demo', label: 'Product demo' },
  { value: 'legal_docs', label: 'Legal docs' },
  { value: 'ip_docs', label: 'IP docs' },
  { value: 'references', label: 'References' },
  { value: 'other', label: 'Other' },
];

const TIER_OPTIONS: Array<{ value: AccessTier; label: string }> = [
  { value: 'basic', label: 'Basic' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'lead', label: 'Lead' },
];

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({
  open,
  onClose,
  onConfirm,
  existingDocuments = [],
  defaultTier,
  defaultFile,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [tier, setTier] = useState<AccessTier>(defaultTier || 'basic');
  const [fileType, setFileType] = useState<DealRoomFileType>('pitch_deck');
  const [previousDocumentId, setPreviousDocumentId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(defaultFile || null);
      setTier(defaultTier || 'basic');
      setFileType('pitch_deck');
      setPreviousDocumentId('');
    }
  }, [open, defaultFile, defaultTier]);

  if (!open) return null;

  const submit = async () => {
    if (!file) {
      toast.error('Choose a file first');
      return;
    }
    setSubmitting(true);
    await onConfirm({
      file,
      access_tier: tier,
      file_type: fileType,
      previous_document_id:
        previousDocumentId === '' ? undefined : Number(previousDocumentId),
    });
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Upload Document</h2>
            <p className="text-xs text-gray-600 mt-1">
              Add a file to this deal room with an access tier.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            File
          </label>
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:border-0 file:rounded-lg file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {file && (
            <div className="text-xs text-gray-500 mt-1">
              {file.name} ({Math.round(file.size / 1024)} KB)
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            Access tier
          </label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as AccessTier)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {TIER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            File type
          </label>
          <select
            value={fileType}
            onChange={(e) => setFileType(e.target.value as DealRoomFileType)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {FILE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            Replaces (optional)
          </label>
          <select
            value={previousDocumentId}
            onChange={(e) =>
              setPreviousDocumentId(
                e.target.value === '' ? '' : Number(e.target.value)
              )
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">New document (no prior version)</option>
            {existingDocuments.map((d) => (
              <option key={d.id} value={d.id}>
                v{d.version ?? 1} — {d.file_name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !file}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentModal;
